import React, { useMemo, useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { financeApi } from '../../services/api/finance.api';
import { useAuth } from '../../contexts/AuthContext';

export const FinanceApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const currentUserId = useMemo(() => auth?.user?.id || '00000000-0000-0000-0000-000000000001', [auth?.user?.id]);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults(prev => ({ ...prev, [name]: { success: true, data: result } }));
    } catch (err: any) {
      const code = err?.details?.status || err?.response?.status || err?.status;
      const url = err?.details?.url || err?.config?.url;
      const method = err?.details?.method || err?.config?.method;
      const backendMsg = err?.message || err?.details?.message;
      const msg =
        code === 404 ? `404 Not Found: ${method?.toUpperCase?.() || ''} ${url || ''}`
        : code === 500 ? `500 Internal Server Error: ${method?.toUpperCase?.() || ''} ${url || ''}`
        : code === 401 ? `401 Unauthorized: проверьте роль и токен`
        : code === 403 ? `403 Forbidden: недостаточно прав`
        : backendMsg || 'Неизвестная ошибка';
      setResults(prev => ({ 
        ...prev, 
        [name]: { 
          success: false,
          error: msg,
          details: { code, url, method }
        } 
      }));
      setError(`${name}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const testTransactions = () => testEndpoint('Transactions List', () => 
    financeApi.transactions.getTransactions({ page: 1, limit: 10 })
  );

  const testBudget = () => testEndpoint('Budget Overview', () => 
    financeApi.budget.getBudget()
  );

  const testBudgetItems = () => testEndpoint('Budget Items', () => 
    financeApi.budget.getBudgetItems()
  );

  const testBudgetSummary = () => testEndpoint('Budget Summary', () => 
    financeApi.budget.getBudgetSummary()
  );

  const testPayments = () => testEndpoint('Payments List', () => 
    financeApi.payments.getPayments({ page: 1, limit: 10 })
  );

  const testPaymentsSummary = () => testEndpoint('Payments Summary', () => 
    financeApi.payments.getPaymentsSummary()
  );

  const testDocuments = () => testEndpoint('Documents Reports', () => 
    financeApi.documents.getReports({ page: 1, limit: 10 })
  );

  const testInvoices = () => testEndpoint('Invoices List', () => 
    financeApi.invoices.getInvoices({ page: 1, limit: 10 })
  );

  const testReferrals = () => testEndpoint('Referrals Stats', () => 
    financeApi.referrals.getReferralStats(currentUserId)
  );

  const testAnalyticsMetrics = () => testEndpoint('Analytics Metrics', () => 
    financeApi.analytics.getMetrics({ userId: currentUserId })
  );

  const testAnalyticsInsights = () => testEndpoint('Analytics Insights', () => 
    financeApi.analytics.getInsights({ userId: currentUserId })
  );

  const testAnalyticsPredictions = () => testEndpoint('Analytics Predictions', () => 
    financeApi.analytics.getPredictions({ userId: currentUserId })
  );

  const testAnalyticsTrends = () => testEndpoint('Analytics Trends', () => 
    financeApi.analytics.getTrends()
  );

  const testAnalyticsReports = () => testEndpoint('Analytics Reports', () => 
    financeApi.analytics.getReports()
  );

  const testUserRole = () => testEndpoint('User Role Check', async () => {
    const user = auth?.user;

    return {
      userId: user?.id,
      role: user?.role,
      email: user?.email,
      hasAccess: ['admin', 'manager', 'main_organizer', 'org_event_manager', 'organizer'].includes(user?.role || ''),
      debug: {
        authKeys: auth ? Object.keys(auth) : 'auth is null/undefined',
        userKeys: user ? Object.keys(user) : 'user is null/undefined',
        authType: typeof auth,
        userType: typeof user
      }
    };
  });

  const testAllEndpoints = async () => {
    // Сначала проверяем роль пользователя
    await testUserRole();
    await testTransactions();
    await testBudget();
    await testBudgetItems();
    await testBudgetSummary();
    await testPayments();
    await testPaymentsSummary();
    await testDocuments();
    await testInvoices();
    await testReferrals();
    await testAnalyticsMetrics();
    await testAnalyticsInsights();
    await testAnalyticsPredictions();
    await testAnalyticsTrends();
    await testAnalyticsReports();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        💰 Тестирование Finance API
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование расширенных endpoints из Finance модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <Button 
            variant="contained" 
            onClick={testAllEndpoints}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Тестировать все endpoints
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testUserRole}
            disabled={loading}
          >
            Проверить роль пользователя
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testTransactions}
            disabled={loading}
          >
            Тест Transactions
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testBudget}
            disabled={loading}
          >
            Тест Budget
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testDocuments}
            disabled={loading}
          >
            Тест Documents
          </Button>
        </Grid>
        <Grid item>
          <Button 
            variant="outlined" 
            onClick={testReferrals}
            disabled={loading}
          >
            Тест Referrals
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        {Object.entries(results).map(([name, result]) => (
          <Grid item xs={12} md={6} key={name}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {name}
                </Typography>
                {result.success ? (
                  <Alert severity="success">
                    ✅ Успешно
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {JSON.stringify(result.data, null, 2)}
                    </Typography>
                  </Alert>
                ) : (
                  <Alert severity="error">
                    ❌ Ошибка: {result.error}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {Object.keys(results).length === 0 && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              Нажмите кнопку для начала тестирования
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default FinanceApiTest;
